import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  reportDatadog,
  reportDatadogAction,
} from '../../../../Root/hooks/reportDatadog';
import { useCallback, useEffect, useState } from 'react';

export interface OnCallSchedule {
  scheduleName: string;
  onCallPerson: string;
}

export interface OnCallTeam {
  teamId: string;
  teamName: string;
  currentSchedules: OnCallSchedule[];
}

export const useOnCall = (teamName?: string) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);

  const [isLoading, setIsLoading] = useState(true);
  const [teamNames, setTeamNames] = useState<string[]>([]);
  const [currentSchedules, setCurrentSchedules] = useState<OnCallSchedule[]>(
    [],
  );
  const [error, setError] = useState<Error | null>(null);

  const fetchWhoIsOnCall = useCallback(async () => {
    setError(null);
    let endpoint = '';
    const startTime = performance.now();

    try {
      const apiUrl = await discoveryApi.getBaseUrl('proxy');
      const apiSuffix = '/ihp/WhoIsOncall';
      endpoint = `${apiUrl}${apiSuffix}`;

      const response = await fetchApi.fetch(endpoint);

      reportDatadogAction(startTime, endpoint, response);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();

      const transformedData: OnCallTeam[] = data.map((team: any) => ({
        teamId: team.team_id,
        teamName: team.team_name,
        currentSchedules: team.schedules.current.map((schedule: any) => ({
          scheduleName: schedule.schedule_name,
          onCallPerson: schedule.oncall_person,
        })),
      }));

      setTeamNames(transformedData.map(team => team.teamName));

      if (teamName) {
        const schedules =
          transformedData.find(team => team.teamName === teamName)
            ?.currentSchedules || [];
        setCurrentSchedules(schedules);
      }
    } catch (e) {
      const errorObject = new Error('Failed to fetch on-call data.', {
        cause: e,
      });
      await reportDatadog(startTime, endpoint, errorObject);

      errorApi.post(errorObject);
      setError(errorObject);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, isLoading]);

  useEffect(() => {
    fetchWhoIsOnCall();
    const interval = setInterval(fetchWhoIsOnCall, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, [fetchWhoIsOnCall]);

  return { isLoading, teamNames, currentSchedules, error };
};
